import { useState, useEffect, useMemo } from "react";
import SbuxSubTabs from "../../../../../components/SbuxTabs/SbuxSubTabs";
import SbuxTextField from "../../../../../components/SubxTextField";
import ClosureMsgTimePicker2 from "./components/ClosureMsgTimePicker2";
import ClosureMsgDailyFrequency from "./components/ClosureMsgDailyFrequency";
import ClosureMsgWeeklyFrequency from "./components/ClosureMessageWeeklyFrequency";
import ClosureMsgMonthlyFrequency from "./components/ClosureMsgMonthlyFrequency";
import ClosureMsgRangeRecurrence from "./components/ClosureMsgRangeRecurrence";
import ClosureMsgYearlyFrequency from "./components/ClosureMsgYearlyFrequency";
import ClosureAudioConfig from "../../common/ClosureAudioConfig";
import SbuxRadioGroup from "../../../../../components/SbuxRadioGroup";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import useTextTranslate from "../../../hooks/useTextTranslate";
import { Box, Divider, InputLabel, FormControl } from "@mui/material";
import dayjs from "../../../../../utils/dayjs";
import type { RepeatPattern } from "../../utils";
import { useAppSelector } from "../../../../../store/types";
import { CM_RECURRENCE_FREQUENCY_LIST } from "../../../../../constants";
import styles from "../../styles";
import useCss from "../../../../../hooks/useCss";
import { getLanguagesSelector } from "../../../../../selectors/userInstanceSelector";
import { getMediaMgtDataSelector } from "../../../../../selectors/mediaMgtSelector";
import { getGlobalConfigUiSelector } from "../../../../../selectors/globalConfigSelectors";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../../slices/mediaMgtSlice";

type MenuOption = { value: string; name: string; [key: string]: unknown };

interface GlobalConfigUi {
  timezones?: MenuOption[];
  weekdays?: MenuOption[];
  months?: MenuOption[];
  closure?: {
    monthly?: MenuOption[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface Language {
  code: string;
  name: string;
  [key: string]: unknown;
}

interface RowData {
  hoursCode?: string;
  groupName?: string;
  category?: string;
  type?: string;
  status?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  startTime?: string;
  endTime?: string;
  startTimeZone?: string;
  endTimeZone?: string;
  description?: string;
  repeatPattern?: RepeatPattern;
  isDailyOption2Selected?: boolean;
  isMonthlyOption2Selected?: boolean;
  isYearlyOption2Selected?: boolean;
  isAudioFileChanged?: boolean;
  languageMessage?: Record<string, unknown>;
  [key: string]: unknown;
}

interface Props {
  row: RowData;
  handleClosureMsgRow: (row: RowData, flag?: boolean) => void;
  isSingleInstance: boolean;
}

const ClosureMsgEdit = ({
  row,
  handleClosureMsgRow,
  isSingleInstance,
}: Props) => {
  const classes = useCss(styles);
  const globalConfigUi = useAppSelector(
    getGlobalConfigUiSelector,
  ) as unknown as GlobalConfigUi | null;
  const languages = useAppSelector(getLanguagesSelector) as Language[] | null;
  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  const initialRow: RowData = { ...row };
  const [description, setDescription] = useState<string>(
    initialRow.description ?? "",
  );
  const [descriptionError, setDescriptionError] = useState(false);
  const [startDate, setStartDate] = useState<Date>(
    dayjs(initialRow.startDate as string).toDate(),
  );
  const [endDate, setEndDate] = useState<Date>(
    dayjs(initialRow.endDate as string).toDate(),
  );
  const [startTime, setStartTime] = useState<string>(
    initialRow.startTime ?? "",
  );
  const [endTime, setEndTime] = useState<string>(initialRow.endTime ?? "");
  const [startTimeZone, setStartTimeZone] = useState<string>(
    initialRow.startTimeZone ?? "",
  );
  const [endTimeZone, setEndTimeZone] = useState<string>(
    initialRow.endTimeZone ?? "",
  );

  const [languageTabId, setLanguageTabId] = useState("en");
  const [recPatternOption, setRecPatternOption] = useState<string | null>(
    initialRow.repeatPattern?.frequency ?? null,
  );

  const rowbj: RowData = useMemo(
    () => ({
      ...row,
      startDate,
      endDate,
      startTime,
      endTime,
      startTimeZone,
      endTimeZone,
    }),
    [row, startDate, endDate, startTime, endTime, startTimeZone, endTimeZone],
  );

  const descriptionPlaceHolder = useTextTranslate("Description");
  const [timeLable, setTimeLabel] = useState("");

  useEffect(() => {
    if (timeLable === "Start Time") {
      const endTimeUpdate = dayjs(startTime, "hh:mm A")
        .add(30, "minutes")
        .format("hh:mm A");
      setEndTime(endTimeUpdate);
      const editRow = {
        ...rowbj,
        startTime: startTime,
        endTime: endTimeUpdate,
      };
      handleClosureMsgRow(editRow);
    }
  }, [startTime, endTime]);

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setDescription(value);
    setDescriptionError(value.length < 1 ? true : false);
    const updateRow = {
      ...rowbj,
      description: value,
    };
    handleClosureMsgRow(updateRow);
  };

  const handleDateChange = (value: { name?: string; date?: Date | null }) => {
    let updateRow: RowData;
    if (value.name === "Start Time") {
      if (value.date) setStartDate(value.date);
      updateRow = {
        ...rowbj,
        startDate: value.date ?? undefined,
        endDate: value.date ?? undefined,
      };
    } else {
      if (value.date) setEndDate(value.date);
      updateRow = { ...rowbj, endDate: value.date ?? undefined };
    }
    handleClosureMsgRow(updateRow);
  };
  const handleStartTimeChange = (value: { time?: string }) => {
    setStartTime(value.time ?? "");
    setTimeLabel("Start Time");
    const updateRow: RowData = { ...rowbj, startTime: value.time };
    handleClosureMsgRow(updateRow);
  };

  const handleEndTimeChange = (value: { time?: string }) => {
    setEndTime(value.time ?? "");
    setTimeLabel("End Time");
    const updateRow: RowData = { ...rowbj, endTime: value.time };
    handleClosureMsgRow(updateRow);
  };
  const handleTimeZoneChange = (timeZone: string) => {
    let updateRow = null;
    setStartTimeZone(timeZone);
    setEndTimeZone(timeZone);
    updateRow = {
      ...rowbj,
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };
    handleClosureMsgRow(updateRow);
  };

  const handleRecPatternOptioneChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRecPatternOption(event.target.value);
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        frequency: event.target.value,
      },
    };
    handleClosureMsgRow(updateRow);
  };

  //Daily
  const selectDailyOption =
    rowbj?.repeatPattern &&
    rowbj.repeatPattern?.frequency === "DAILY" &&
    Number(rowbj.repeatPattern.interval)
      ? "dailyOption1EveryDays"
      : "dailyOption2EveryWeekDay";
  const [dailyOption, setDailyOption] = useState(selectDailyOption);
  const [dailyOptoin1InputValue, setDailyOptoin1InputValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "DAILY" &&
      rowbj.repeatPattern.interval
      ? Number(rowbj.repeatPattern.interval)
      : 1,
  );

  const handleDailyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === "dailyOption2EveryWeekDay") {
      rowbj.isDailyOption2Selected = true;
      handleClosureMsgRow(rowbj);
    } else {
      rowbj.isDailyOption2Selected = false;
      handleClosureMsgRow(rowbj);
    }

    setDailyOption(event.target.value);
  };

  const handleDailyOptoin1InputValue = (value: string | number) => {
    setDailyOptoin1InputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  //Weelky

  const [weeklyRecEveryInputValue, setWeeklyRecEveryInputValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "WEEKLY" &&
      rowbj.repeatPattern.interval
      ? Number(rowbj.repeatPattern.interval)
      : 1,
  );
  const [weeklyRecDaysChecked, setWeeklyRecDaysChecked] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "WEEKLY" &&
      rowbj.repeatPattern.byWeekDay
      ? rowbj.repeatPattern.byWeekDay
      : ["MO"],
  );

  const handleWeeklyRecEveryInputValueChange = (value: string | number) => {
    setWeeklyRecEveryInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let data: string[];
    if (event.target.checked) {
      const arr = [...weeklyRecDaysChecked];
      arr.push(event.target.name);
      data = [...arr];
      setWeeklyRecDaysChecked(data);
    } else {
      data = weeklyRecDaysChecked.filter(
        (f: string) => f !== event.target.name,
      );
      setWeeklyRecDaysChecked(data);
    }
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byWeekDay: data,
      },
    };
    handleClosureMsgRow(updateRow);
  };
  //Monthly

  const selectMonhtlyOption =
    rowbj?.repeatPattern &&
    rowbj.repeatPattern?.frequency === "MONTHLY" &&
    rowbj.repeatPattern.byWeekDay &&
    rowbj.repeatPattern.byWeekDayN
      ? "monthlyOption2The"
      : "monthlyOption1Day";
  const [monthlyOption, setMonthlyOption] = useState(selectMonhtlyOption);
  const [monthlyRecDayOfValue, setMonthlyRecDayOfValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "MONTHLY" &&
      rowbj.repeatPattern?.byWeekDayN
      ? Number(rowbj.repeatPattern.byWeekDayN)
      : 1,
  );
  const [monthlyRecWeekDays, setMonthlyRecWeekDays] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "MONTHLY" &&
      rowbj.repeatPattern?.byWeekDay
      ? rowbj.repeatPattern.byWeekDay
      : ["MO"],
  );

  const [
    monthlyRecOption1EveryDayInputValue,
    setMonthlyRecOption1EveryDayInputValue,
  ] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "MONTHLY" &&
      rowbj.repeatPattern?.byDay
      ? Number(rowbj.repeatPattern.byDay)
      : 1,
  );

  const [
    monthlyRecOption1EveryMonthsInputValue,
    setMonthlyRecOption1EveryMonthsInputValue,
  ] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "MONTHLY" &&
      rowbj.repeatPattern?.interval
      ? Number(rowbj.repeatPattern.interval)
      : 1,
  );

  const [
    monthlyRecOption2EveryMonthsInputValue,
    setMonthlyMonthlyRecOption2EveryMonthsInputValue,
  ] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "MONTHLY" &&
      rowbj.repeatPattern.byWeekDayN &&
      rowbj.repeatPattern?.interval
      ? Number(rowbj.repeatPattern.interval)
      : 1,
  );

  const handleMonthlyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setMonthlyOption(event.target.value);
    if (event.target.value === "monthlyOption2The") {
      rowbj.isMonthlyOption2Selected = true;
      handleClosureMsgRow(rowbj);
    } else {
      rowbj.isMonthlyOption2Selected = false;
      handleClosureMsgRow(rowbj);
    }
  };

  const handleMonthlyRecDayOfValueChange = (value: string | number) => {
    setMonthlyRecDayOfValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byWeekDayN: value,
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const handleMonthlyRecWeekDaysChange = (value: string) => {
    setMonthlyRecWeekDays([value]);
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byWeekDay: [value],
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const handleMonthlyRecOption1EveryDayInputValueChange = (
    value: string | number,
  ) => {
    setMonthlyRecOption1EveryDayInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byDay: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };
  const handleMonthlyRecOption1EveryMonthsInputValueChange = (
    value: string | number,
  ) => {
    setMonthlyRecOption1EveryMonthsInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };
  const handleMonthlyRecOption2EveryMonthsInputValueChange = (
    value: string | number,
  ) => {
    setMonthlyMonthlyRecOption2EveryMonthsInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  //Yearly
  const selectYearlyOption =
    rowbj?.repeatPattern &&
    rowbj.repeatPattern?.frequency === "YEARLY" &&
    rowbj.repeatPattern.byDay
      ? "yearlyOption1On"
      : "yearlyOption2OnThe";
  const [yearlyOption, setYearlyOption] = useState(selectYearlyOption);

  const handleYearlyOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setYearlyOption(event.target.value);
    if (event.target.value === "yearlyOption2OnThe") {
      rowbj.isYearlyOption2Selected = true;
      handleClosureMsgRow(rowbj);
    } else {
      rowbj.isYearlyOption2Selected = false;
      handleClosureMsgRow(rowbj);
    }
  };

  const [yearlyRecEveryInputValue, setYearlyRecEveryInputValue] = useState(
    rowbj?.repeatPattern && rowbj.repeatPattern?.frequency === "YEARLY"
      ? Number(rowbj.repeatPattern.interval)
      : 1,
  );

  const handleYearlyRecEveryInputValueChange = (value: string | number) => {
    setYearlyRecEveryInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        interval: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  //Option 1 -----Start
  const [yearlyOption1MonthValue, setYearlyOption1MonthValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "YEARLY" &&
      rowbj.repeatPattern.byMonth
      ? rowbj.repeatPattern.byMonth
      : 1,
  );
  const handleYearlyOption1MonthValueChange = (value: string | number) => {
    setYearlyOption1MonthValue(value);
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byMonth: String(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const [yearlyOption1OnInputValue, setYearlyOption1OnInputValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "YEARLY" &&
      rowbj.repeatPattern.byDay
      ? rowbj.repeatPattern.byDay
      : 1,
  );
  const handleYearlyOption1OnInputValueChange = (value: string | number) => {
    setYearlyOption1OnInputValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byDay: Number(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const [yearlyRecDayOfValue, setYearlyRecDayOfValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "YEARLY" &&
      rowbj.repeatPattern.byWeekDayN
      ? rowbj.repeatPattern.byWeekDayN
      : 1,
  );
  const [yearlyRecWeekDays, setYearlyRecWeekDays] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "YEARLY" &&
      rowbj.repeatPattern.byWeekDay
      ? rowbj.repeatPattern.byWeekDay
      : ["MO"],
  );
  const handleYearlyRecDayOfValueChange = (value: string | number) => {
    setYearlyRecDayOfValue(Number(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byWeekDayN: value,
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const handleYearlyRecWeekDaysChange = (value: string) => {
    setYearlyRecWeekDays([value]);
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byWeekDay: [value],
      },
    };
    handleClosureMsgRow(updateRow);
  };
  const [yearlyOption2MonthValue, setYearlyOption2MonthValue] = useState(
    rowbj?.repeatPattern &&
      rowbj.repeatPattern?.frequency === "YEARLY" &&
      rowbj.repeatPattern.byMonth
      ? rowbj.repeatPattern.byMonth
      : 1,
  );
  const handleYearlyOption2MonthValueChange = (value: string | number) => {
    setYearlyOption2MonthValue(String(value));
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        byMonth: String(value),
      },
    };
    handleClosureMsgRow(updateRow);
  };

  //Recurrence Date

  const selectEndByOption =
    rowbj?.repeatPattern && rowbj.repeatPattern?.until
      ? "endByEndDate"
      : "endByNoDate";

  const [endByOption, setEndByOption] = useState(selectEndByOption);

  const handleEndByOptionsValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEndByOption(event.target.value);
    handleClosureMsgRow(rowbj);
  };
  const [endByEndDate, setEndByEndDate] = useState<Date | null>(
    rowbj?.repeatPattern && rowbj.repeatPattern?.until
      ? dayjs(rowbj.repeatPattern.until, "YYYY-MM-DD").toDate()
      : dayjs().toDate(),
  );

  const handleEndByEndDateChange = (value: Date | null) => {
    setEndByEndDate(value);
    const endByEndDate = dayjs
      .tz(
        `${dayjs(value).format("YYYY-MM-DD")} ${endTime}`,
        "YYYY-MM-DD hh:mm a",
        endTimeZone,
      )
      .toISOString();
    const updateRow = {
      ...rowbj,
      repeatPattern: {
        ...(rowbj.repeatPattern ?? {}),
        until: endByEndDate,
      },
    };
    handleClosureMsgRow(updateRow);
  };

  const handleSubTabChange = (newValue: string) => {
    setLanguageTabId(newValue);
  };
  const handleLanguageMediaFile = (
    value: string,
    isAudioFileChanged = false,
  ) => {
    const messageLanguageTabId = `message_${languageTabId}`;
    const existingLanguages: Record<string, unknown> =
      Object.keys(rowbj).length > 0
        ? Object.keys(rowbj)
            .filter((a) => a.includes("message"))
            .reduce<Record<string, unknown>>((acc, cur) => {
              acc[cur] = rowbj[cur];
              return acc;
            }, {})
        : {};
    let updateLanguageMessage: Record<string, unknown> = {
      ...existingLanguages,
    };
    let updateRow: Record<string, unknown> = { ...rowbj };
    if (value) {
      const mediaItems = getMediaItems();
      const mediaInfo = mediaItems.find((m) => m.filePath === value);

      if (mediaInfo && mediaInfo.filePath) {
        const newLanguageMessage = {
          [`${messageLanguageTabId}`]: {
            type: "AUDIO",
            value: mediaInfo.filePath,
            accessType: mediaInfo.type,
          },
        };

        updateLanguageMessage = {
          ...existingLanguages,
          ...newLanguageMessage,
        };
        updateRow = {
          ...rowbj,
          ...updateLanguageMessage,
          languageMessage: { ...updateLanguageMessage },
          isAudioFileChanged: isAudioFileChanged,
        };
      }
    } else {
      if (updateLanguageMessage[messageLanguageTabId]) {
        delete updateLanguageMessage[messageLanguageTabId];
      }
      updateRow = {
        ...rowbj,
        ...updateLanguageMessage,
        languageMessage: { ...updateLanguageMessage },
        isAudioFileChanged: isAudioFileChanged,
      };
      if (updateRow[messageLanguageTabId]) {
        delete updateRow[messageLanguageTabId];
      }
    }

    handleClosureMsgRow(updateRow);
  };

  const subTabLanguages = useMemo(
    () => (languages ?? []).map((l) => ({ name: l.name, value: l.code })),
    [languages],
  );

  return (
    <Box>
      <Box
        component="form"
        className={classes.formControl}
        noValidate
        autoComplete="off"
      >
        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Business"}</SbuxTranslate>}
            disabled
            preventAutoFocus
            value={rowbj.groupName}
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Category"}</SbuxTranslate>}
            disabled
            preventAutoFocus
            value={rowbj.category}
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Description"}</SbuxTranslate>}
            placeholder={descriptionPlaceHolder}
            value={description}
            handleChange={handleDescriptionChange}
            errorMessage={
              <SbuxTranslate>
                {"Description required at least 1 character"}
              </SbuxTranslate>
            }
            isError={descriptionError}
            disabled={rowbj.status === "Expired" || isSingleInstance}
            required={!isSingleInstance}
          />
        </Box>

        <Box>
          <SbuxTextField
            label={<SbuxTranslate>{"Series Settings"}</SbuxTranslate>}
            disabled
            preventAutoFocus
            value={rowbj.type}
          />
        </Box>

        <Divider className={classes.topDiv} />

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"Start Date and Time"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <ClosureMsgTimePicker2
                type={1}
                startDate={startDate}
                endDate={endDate}
                startTimeValue={startTime}
                endTimeValue={endTime}
                timeZoneValue={startTimeZone}
                timeZoneList={globalConfigUi?.timezones}
                handleDateChange={handleDateChange}
                handleStartTimeChange={handleStartTimeChange}
                handleTimeZoneChange={handleTimeZoneChange}
                row={rowbj as Record<string, unknown>}
              />
            </Box>
          </FormControl>
        </Box>

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"End Date and Time"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <ClosureMsgTimePicker2
                type={2}
                startDate={startDate}
                endDate={endDate}
                startTimeValue={startTime}
                endTimeValue={endTime}
                timeZoneValue={endTimeZone}
                timeZoneList={globalConfigUi?.timezones}
                handleDateChange={handleDateChange}
                handleEndTimeChange={handleEndTimeChange}
                handleTimeZoneChange={handleTimeZoneChange}
                row={rowbj as Record<string, unknown>}
              />
            </Box>
          </FormControl>
        </Box>

        {rowbj.type === "Repeat" && (
          <>
            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"Repeat Pattern"}</SbuxTranslate>
                </InputLabel>

                <Box className={classes.repeatPatternContainer}>
                  <Box sx={{ width: 100 }}>
                    <SbuxRadioGroup
                      radioButtonList={CM_RECURRENCE_FREQUENCY_LIST}
                      value={recPatternOption}
                      handleRadioButtonChange={handleRecPatternOptioneChange}
                    />
                  </Box>

                  <Box sx={{ width: "1px" }}>
                    <Divider
                      orientation="vertical"
                      sx={{ backgroundColor: "#006241" }}
                    />
                  </Box>
                  <Box sx={{ width: 100 }}>
                    {recPatternOption === "DAILY" && (
                      <ClosureMsgDailyFrequency
                        dailyOption={dailyOption} //Raido button value for Daily
                        handleDailyOptionsValueChange={
                          handleDailyOptionsValueChange
                        }
                        dailyOptoin1InputValue={dailyOptoin1InputValue}
                        handleDailyOptoin1InputValue={
                          handleDailyOptoin1InputValue
                        }
                      />
                    )}

                    {recPatternOption === "WEEKLY" && (
                      <ClosureMsgWeeklyFrequency
                        isEdit={true}
                        weeklyRecDays={weeklyRecDaysChecked}
                        weeklyRecEveryInputValue={weeklyRecEveryInputValue}
                        handleCheckboxChange={handleCheckboxChange}
                        handleWeeklyRecEveryInputValueChange={
                          handleWeeklyRecEveryInputValueChange
                        }
                      />
                    )}

                    {recPatternOption === "MONTHLY" && (
                      <ClosureMsgMonthlyFrequency
                        monthlyOption={monthlyOption} //Raido button value for Monthly
                        handleMonthlyOptionsValueChange={
                          handleMonthlyOptionsValueChange
                        }
                        monthlyRecOption1EveryDayInputValue={
                          monthlyRecOption1EveryDayInputValue
                        }
                        monthlyRecOption1EveryMonthsInputValue={
                          monthlyRecOption1EveryMonthsInputValue
                        }
                        monthlyRecOption2EveryMonthsInputValue={
                          monthlyRecOption2EveryMonthsInputValue
                        }
                        monthlyRecDayOfList={globalConfigUi?.closure?.monthly}
                        monthlyRecDayOfValue={monthlyRecDayOfValue} //First, Last and etc
                        handleMonthlyRecDayOfValueChange={
                          handleMonthlyRecDayOfValueChange
                        }
                        monthlyRecWeekDaysList={globalConfigUi?.weekdays}
                        monthlyRecWeekDays={
                          Array.isArray(monthlyRecWeekDays)
                            ? monthlyRecWeekDays[0]
                            : monthlyRecWeekDays
                        } //dropdown value (Sunday, Monday)
                        handleMonthlyRecWeekDaysChange={
                          handleMonthlyRecWeekDaysChange
                        }
                        handleMonthlyRecOption1EveryDayInputValueChange={
                          handleMonthlyRecOption1EveryDayInputValueChange
                        }
                        handleMonthlyRecOption1EveryMonthsInputValueChange={
                          handleMonthlyRecOption1EveryMonthsInputValueChange
                        }
                        handleMonthlyRecOption2EveryMonthsInputValueChange={
                          handleMonthlyRecOption2EveryMonthsInputValueChange
                        }
                      />
                    )}
                    {recPatternOption === "YEARLY" && (
                      <ClosureMsgYearlyFrequency
                        yearlyOption={yearlyOption}
                        yearlyMonthList={globalConfigUi?.months}
                        yearlyRecWeekDaysList={globalConfigUi?.closure?.monthly}
                        yearlyWeekDaysList={globalConfigUi?.weekdays}
                        handleYearlyOptionsValueChange={
                          handleYearlyOptionsValueChange
                        }
                        yearlyRecEveryInputValue={yearlyRecEveryInputValue}
                        handleYearlyRecEveryInputValueChange={
                          handleYearlyRecEveryInputValueChange
                        }
                        yearlyOption1MonthValue={yearlyOption1MonthValue}
                        handleYearlyOption1MonthValueChange={
                          handleYearlyOption1MonthValueChange
                        }
                        yearlyOption1OnInputValue={yearlyOption1OnInputValue}
                        handleYearlyOption1OnInputValueChange={
                          handleYearlyOption1OnInputValueChange
                        }
                        yearlyOption2MonthValue={yearlyOption2MonthValue}
                        handleYearlyOption2MonthValueChange={
                          handleYearlyOption2MonthValueChange
                        }
                        yearlyRecDayOfValue={String(yearlyRecDayOfValue)}
                        handleYearlyRecDayOfValueChange={
                          handleYearlyRecDayOfValueChange
                        }
                        yearlyRecWeekDays={
                          Array.isArray(yearlyRecWeekDays)
                            ? yearlyRecWeekDays[0]
                            : yearlyRecWeekDays
                        }
                        handleYearlyRecWeekDaysChange={
                          handleYearlyRecWeekDaysChange
                        }
                      />
                    )}
                  </Box>
                </Box>
              </FormControl>
            </Box>

            <Box className="fullWidth">
              <FormControl>
                <InputLabel>
                  <SbuxTranslate>{"End by"}</SbuxTranslate>
                </InputLabel>

                <Box>
                  <ClosureMsgRangeRecurrence
                    endByOption={endByOption}
                    endByEndDate={endByEndDate}
                    handleEndByEndDateChange={handleEndByEndDateChange}
                    handleEndByOptionsValueChange={
                      handleEndByOptionsValueChange
                    }
                  />
                </Box>
              </FormControl>
            </Box>
          </>
        )}

        <Divider />

        <Box className="fullWidth">
          <FormControl>
            <InputLabel>
              <SbuxTranslate>{"Audio Configuration"}</SbuxTranslate>
            </InputLabel>

            <Box>
              <SbuxTranslate>
                {rowbj.type === "Repeat" && isSingleInstance && (
                  <Box
                    sx={{
                      // should be moved into a class
                      color: "#006241",
                      fontSize: 12, // no hardwire fontSize
                      fontWeight: 600,
                      width: "50vw", // no calc based on viewport
                    }}
                  >
                    Your audio file will be updated for the entire series
                  </Box>
                )}
              </SbuxTranslate>

              <SbuxSubTabs
                tabList={subTabLanguages}
                tabId={languageTabId}
                handleTabClick={(tab: { value: string }) =>
                  handleSubTabChange(tab.value)
                }
                width={"100%"}
              />
              <Box className={classes.tabContainer}>
                <ClosureAudioConfig
                  languageId={languageTabId}
                  handleLanguageMediaFile={handleLanguageMediaFile}
                  row={rowbj as Record<string, unknown>}
                  disabled={rowbj.status === "Expired"}
                />
              </Box>
            </Box>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ClosureMsgEdit;
